import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, Pencil, Trash2, Send, Clock, FileText, AlertTriangle, ExternalLink, Eye, Mail, Users, Pin } from "lucide-react";

type AnnouncementType = "general" | "update" | "event" | "policy" | "maintenance";
type UrgencyLevel = "low" | "normal" | "high" | "critical";
type AnnouncementStatus = "draft" | "scheduled" | "sent";
type AudienceRole = "entrepreneur" | "mentor" | "admin" | "instructor";

interface CommunityAnnouncement {
  id: string;
  title: string;
  body: string;
  announcement_type: AnnouncementType;
  urgency_level: UrgencyLevel;
  cta_label: string | null;
  cta_url: string | null;
  status: AnnouncementStatus;
  scheduled_at: string | null;
  sent_at: string | null;
  send_email: boolean;
  is_pinned: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

const ANNOUNCEMENT_TYPE_OPTIONS: { value: AnnouncementType; label: string }[] = [
  { value: "general", label: "General" },
  { value: "update", label: "Platform Update" },
  { value: "event", label: "Event" },
  { value: "policy", label: "Policy" },
  { value: "maintenance", label: "Maintenance" },
];

const URGENCY_LEVEL_OPTIONS: { value: UrgencyLevel; label: string; color: string }[] = [
  { value: "low", label: "Low", color: "bg-muted text-muted-foreground" },
  { value: "normal", label: "Normal", color: "bg-secondary text-secondary-foreground" },
  { value: "high", label: "High", color: "bg-warning/20 text-warning border-warning/30" },
  { value: "critical", label: "Critical", color: "bg-destructive/20 text-destructive border-destructive/30" },
];

const STATUS_OPTIONS: { value: AnnouncementStatus; label: string }[] = [
  { value: "draft", label: "Save as Draft" },
  { value: "sent", label: "Send Now" },
  { value: "scheduled", label: "Schedule" },
];

const AUDIENCE_ROLE_OPTIONS: { value: AudienceRole; label: string }[] = [
  { value: "entrepreneur", label: "Entrepreneurs" },
  { value: "mentor", label: "Mentors" },
  { value: "admin", label: "Admins" },
  { value: "instructor", label: "Instructors" },
];

interface FormData {
  title: string;
  body: string;
  announcement_type: AnnouncementType;
  urgency_level: UrgencyLevel;
  cta_label: string;
  cta_url: string;
  status: AnnouncementStatus;
  scheduled_at: string;
  audience_roles: AudienceRole[];
  send_email: boolean;
  is_pinned: boolean;
}

const initialFormData: FormData = {
  title: "",
  body: "",
  announcement_type: "general",
  urgency_level: "normal",
  cta_label: "",
  cta_url: "",
  status: "draft",
  scheduled_at: "",
  audience_roles: ["entrepreneur", "mentor", "admin", "instructor"],
  send_email: false,
  is_pinned: false,
};

export function CommunityAnnouncementsManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSendConfirmDialog, setShowSendConfirmDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<CommunityAnnouncement | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [pendingSendAction, setPendingSendAction] = useState(false);

  // Fetch announcements with audiences (admin sees all)
  const { data: announcements, isLoading } = useQuery({
    queryKey: ["community_announcements_admin"],
    queryFn: async () => {
      // Fetch announcements with author info
      const { data: announcementData, error } = await supabase
        .from("community_announcements")
        .select("*")
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });
      
      if (error) throw error;

      // Fetch author names for all announcements
      const authorIds = [...new Set(announcementData.map(a => a.created_by).filter(Boolean))];
      let authorMap = new Map<string, string>();
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", authorIds);
        if (profiles) {
          profiles.forEach(p => authorMap.set(p.id, p.full_name));
        }
      }
      
      // Fetch audiences for all announcements using database function
      const announcementIds = announcementData.map(a => a.id);
      let audienceData: { announcement_id: string; audience_role: string }[] | null = null;
      try {
        const { data } = await supabase.rpc("get_announcement_audiences", { announcement_ids: announcementIds });
        audienceData = data;
      } catch {
        // Function may not exist yet, continue with defaults
      }
      
      // If RPC doesn't exist yet, fall back to default all audiences
      const audienceMap = new Map<string, string[]>();
      if (audienceData) {
        (audienceData as { announcement_id: string; audience_role: string }[]).forEach(a => {
          const existing = audienceMap.get(a.announcement_id) || [];
          audienceMap.set(a.announcement_id, [...existing, a.audience_role]);
        });
      }
      
      // Merge audiences and author into announcements
      return announcementData.map(announcement => ({
        ...announcement,
        audiences: audienceMap.get(announcement.id) || ["entrepreneur", "mentor", "admin", "instructor"],
        author_name: authorMap.get(announcement.created_by) || "Unknown",
      })) as (CommunityAnnouncement & { audiences: string[]; author_name: string })[];
    },
  });

  // Create announcement mutation
  const createAnnouncement = useMutation({
    mutationFn: async (data: FormData) => {
      const isSending = data.status === "sent";
      
      const { data: result, error } = await supabase
        .from("community_announcements")
        .insert({
          title: data.title,
          body: data.body,
          announcement_type: data.announcement_type,
          urgency_level: data.urgency_level,
          cta_label: data.cta_label || null,
          cta_url: data.cta_url || null,
          status: data.status,
          scheduled_at: data.status === "scheduled" ? data.scheduled_at : null,
          sent_at: isSending ? new Date().toISOString() : null,
          send_email: data.send_email,
          is_pinned: data.is_pinned,
          created_by: user?.id,
        })
        .select()
        .single();
      
      if (error) throw error;

      // Insert audience roles using raw insert
      if (data.audience_roles.length > 0) {
        const audienceInserts = data.audience_roles.map(role => ({
          announcement_id: result.id,
          audience_role: role,
        }));
        // Use any to bypass type checking for new table
        await (supabase as any).from("community_announcement_audiences").insert(audienceInserts);
      }

      // Log the action
      await supabase.from("announcement_audit_log").insert({
        announcement_id: result.id,
        action: isSending ? "sent" : "created",
        admin_user_id: user?.id,
        details: { title: data.title, status: data.status, audiences: data.audience_roles },
      });

      // Send emails if requested and sending now
      if (isSending && data.send_email && data.audience_roles.length > 0) {
        try {
          await supabase.functions.invoke("send-community-announcement-email", {
            body: {
              announcement_id: result.id,
              audience_roles: data.audience_roles,
            },
          });
        } catch (emailError) {
          console.error("Error sending emails:", emailError);
          toast.error("Announcement sent but email delivery failed");
        }
      }

      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["community_announcements_admin"] });
      queryClient.invalidateQueries({ queryKey: ["community_announcements_member"] });
      const message = variables.status === "sent" 
        ? variables.send_email 
          ? "Announcement sent and emails delivered!" 
          : "Announcement sent to all members!"
        : "Announcement saved!";
      toast.success(message);
      setShowCreateDialog(false);
      setFormData(initialFormData);
    },
    onError: (error) => {
      toast.error("Failed to create announcement: " + error.message);
    },
  });

  // Update announcement mutation
  const updateAnnouncement = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormData }) => {
      const isSending = data.status === "sent" && selectedAnnouncement?.status !== "sent";
      
      const { error } = await supabase
        .from("community_announcements")
        .update({
          title: data.title,
          body: data.body,
          announcement_type: data.announcement_type,
          urgency_level: data.urgency_level,
          cta_label: data.cta_label || null,
          cta_url: data.cta_url || null,
          status: data.status,
          scheduled_at: data.status === "scheduled" ? data.scheduled_at : null,
          sent_at: isSending ? new Date().toISOString() : selectedAnnouncement?.sent_at,
          send_email: data.send_email,
          is_pinned: data.is_pinned,
        })
        .eq("id", id);
      
      if (error) throw error;

      // Update audience roles - delete old and insert new
      await (supabase as any).from("community_announcement_audiences").delete().eq("announcement_id", id);
      if (data.audience_roles.length > 0) {
        const audienceInserts = data.audience_roles.map(role => ({
          announcement_id: id,
          audience_role: role,
        }));
        await (supabase as any).from("community_announcement_audiences").insert(audienceInserts);
      }

      // Log the action
      await supabase.from("announcement_audit_log").insert({
        announcement_id: id,
        action: isSending ? "sent" : "updated",
        admin_user_id: user?.id,
        details: { title: data.title, status: data.status, audiences: data.audience_roles },
      });

      // Send emails if requested and sending now
      if (isSending && data.send_email && data.audience_roles.length > 0) {
        try {
          await supabase.functions.invoke("send-community-announcement-email", {
            body: {
              announcement_id: id,
              audience_roles: data.audience_roles,
            },
          });
        } catch (emailError) {
          console.error("Error sending emails:", emailError);
          toast.error("Announcement sent but email delivery failed");
        }
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["community_announcements_admin"] });
      queryClient.invalidateQueries({ queryKey: ["community_announcements_member"] });
      const isSending = variables.data.status === "sent" && selectedAnnouncement?.status !== "sent";
      const message = isSending 
        ? variables.data.send_email 
          ? "Announcement sent and emails delivered!" 
          : "Announcement sent to all members!"
        : "Announcement updated!";
      toast.success(message);
      setShowEditDialog(false);
      setSelectedAnnouncement(null);
      setFormData(initialFormData);
    },
    onError: (error) => {
      toast.error("Failed to update announcement: " + error.message);
    },
  });

  // Delete announcement mutation
  const deleteAnnouncement = useMutation({
    mutationFn: async (id: string) => {
      // Log before delete
      await supabase.from("announcement_audit_log").insert({
        announcement_id: id,
        action: "deleted",
        admin_user_id: user?.id,
        details: { title: selectedAnnouncement?.title },
      });

      const { error } = await supabase
        .from("community_announcements")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["community_announcements_admin"] });
      queryClient.invalidateQueries({ queryKey: ["community_announcements_member"] });
      toast.success("Announcement deleted");
      setShowDeleteDialog(false);
      setSelectedAnnouncement(null);
    },
    onError: (error) => {
      toast.error("Failed to delete announcement: " + error.message);
    },
  });

  const handleEdit = (announcement: CommunityAnnouncement & { audiences?: string[] }) => {
    setSelectedAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      body: announcement.body,
      announcement_type: announcement.announcement_type,
      urgency_level: announcement.urgency_level,
      cta_label: announcement.cta_label || "",
      cta_url: announcement.cta_url || "",
      status: announcement.status,
      scheduled_at: announcement.scheduled_at || "",
      audience_roles: (announcement.audiences || ["entrepreneur", "mentor", "admin", "instructor"]) as AudienceRole[],
      send_email: announcement.send_email || false,
      is_pinned: announcement.is_pinned || false,
    });
    setShowEditDialog(true);
  };

  const handleDelete = (announcement: CommunityAnnouncement) => {
    setSelectedAnnouncement(announcement);
    setShowDeleteDialog(true);
  };

  const handlePreview = (announcement: CommunityAnnouncement) => {
    setSelectedAnnouncement(announcement);
    setShowPreviewDialog(true);
  };

  const handleSubmit = (isEdit: boolean) => {
    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.status === "scheduled" && !formData.scheduled_at) {
      toast.error("Please select a scheduled date and time");
      return;
    }

    // Show confirmation for sending
    if (formData.status === "sent") {
      setPendingSendAction(true);
      setShowSendConfirmDialog(true);
      return;
    }

    if (isEdit && selectedAnnouncement) {
      updateAnnouncement.mutate({ id: selectedAnnouncement.id, data: formData });
    } else {
      createAnnouncement.mutate(formData);
    }
  };

  const handleConfirmSend = () => {
    if (showEditDialog && selectedAnnouncement) {
      updateAnnouncement.mutate({ id: selectedAnnouncement.id, data: formData });
    } else {
      createAnnouncement.mutate(formData);
    }
    setShowSendConfirmDialog(false);
    setPendingSendAction(false);
  };

  const getStatusBadge = (status: AnnouncementStatus) => {
    switch (status) {
      case "draft":
        return <Badge variant="outline"><FileText className="h-3 w-3 mr-1" />Draft</Badge>;
      case "scheduled":
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Scheduled</Badge>;
      case "sent":
        return <Badge className="bg-success/20 text-success border-success/30"><Send className="h-3 w-3 mr-1" />Sent</Badge>;
    }
  };

  const getUrgencyBadge = (urgency: UrgencyLevel) => {
    const option = URGENCY_LEVEL_OPTIONS.find(o => o.value === urgency);
    return <Badge className={option?.color}>{option?.label}</Badge>;
  };

  const closeCreateDialog = () => {
    setShowCreateDialog(false);
    setFormData(initialFormData);
  };

  const closeEditDialog = () => {
    setShowEditDialog(false);
    setSelectedAnnouncement(null);
    setFormData(initialFormData);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Community Announcements</h2>
          <p className="text-sm text-muted-foreground">
            Send official platform updates to all members
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={(open) => open ? setShowCreateDialog(true) : closeCreateDialog()}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Community Announcement</DialogTitle>
              <DialogDescription>
                This announcement will be visible to all members once sent.
              </DialogDescription>
            </DialogHeader>
            <AnnouncementForm
              formData={formData}
              setFormData={setFormData}
              onSubmit={() => handleSubmit(false)}
              isSubmitting={createAnnouncement.isPending}
              onCancel={closeCreateDialog}
            />
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : announcements && announcements.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>All Announcements</CardTitle>
            <CardDescription>
              Manage your platform announcements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Author</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Urgency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {announcements.map((announcement) => (
                  <TableRow key={announcement.id}>
                    <TableCell className="font-medium max-w-[200px]">
                      <div className="flex items-center gap-2">
                        {announcement.is_pinned && <Pin className="h-4 w-4 text-primary flex-shrink-0" />}
                        <span className="truncate">{announcement.title}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {announcement.author_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {ANNOUNCEMENT_TYPE_OPTIONS.find(o => o.value === announcement.announcement_type)?.label}
                      </Badge>
                    </TableCell>
                    <TableCell>{getUrgencyBadge(announcement.urgency_level)}</TableCell>
                    <TableCell>{getStatusBadge(announcement.status)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {announcement.sent_at 
                        ? format(new Date(announcement.sent_at), "MMM d, yyyy")
                        : format(new Date(announcement.created_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handlePreview(announcement)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(announcement)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(announcement)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Send className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No announcements yet</p>
            <p className="text-sm text-muted-foreground">Create your first community announcement</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => open ? null : closeEditDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Announcement</DialogTitle>
            <DialogDescription>
              Update this community announcement.
            </DialogDescription>
          </DialogHeader>
          <AnnouncementForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={() => handleSubmit(true)}
            isSubmitting={updateAnnouncement.isPending}
            onCancel={closeEditDialog}
            isEdit
            currentStatus={selectedAnnouncement?.status}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Announcement</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedAnnouncement?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => selectedAnnouncement && deleteAnnouncement.mutate(selectedAnnouncement.id)}
            >
              {deleteAnnouncement.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Confirmation Dialog */}
      <AlertDialog open={showSendConfirmDialog} onOpenChange={setShowSendConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Confirm Send
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  You are about to send this announcement to{" "}
                  <strong>
                    {formData.audience_roles.length === 4 
                      ? "all platform members" 
                      : formData.audience_roles.map(role => 
                          AUDIENCE_ROLE_OPTIONS.find(o => o.value === role)?.label
                        ).filter(Boolean).join(", ")
                    }
                  </strong>.
                </p>
                <p className="mt-2">
                  This action will make the announcement immediately visible to the selected audience.
                </p>
                <p className="mt-4">Are you sure you want to proceed?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingSendAction(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSend}>
              {(createAnnouncement.isPending || updateAnnouncement.isPending) 
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> 
                : <Send className="h-4 w-4 mr-2" />}
              {formData.audience_roles.length === 4 ? "Send to All Members" : "Send Announcement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
          </DialogHeader>
          {selectedAnnouncement && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                {getStatusBadge(selectedAnnouncement.status)}
                {getUrgencyBadge(selectedAnnouncement.urgency_level)}
                <Badge variant="outline">
                  {ANNOUNCEMENT_TYPE_OPTIONS.find(o => o.value === selectedAnnouncement.announcement_type)?.label}
                </Badge>
              </div>
              <div>
                <h3 className="text-lg font-semibold">{selectedAnnouncement.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  From the EAB Team • {format(new Date(selectedAnnouncement.created_at), "MMMM d, yyyy")}
                </p>
              </div>
              <p className="text-muted-foreground whitespace-pre-wrap">{selectedAnnouncement.body}</p>
              {selectedAnnouncement.cta_label && selectedAnnouncement.cta_url && (
                <Button variant="outline" asChild>
                  <a href={selectedAnnouncement.cta_url} target="_blank" rel="noopener noreferrer">
                    {selectedAnnouncement.cta_label}
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </a>
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Extracted form component
function AnnouncementForm({
  formData,
  setFormData,
  onSubmit,
  isSubmitting,
  onCancel,
  isEdit = false,
  currentStatus,
}: {
  formData: FormData;
  setFormData: (data: FormData) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  onCancel: () => void;
  isEdit?: boolean;
  currentStatus?: AnnouncementStatus;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          placeholder="Announcement title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="body">Message *</Label>
        <Textarea
          id="body"
          placeholder="Write your announcement message..."
          value={formData.body}
          onChange={(e) => setFormData({ ...formData, body: e.target.value })}
          rows={6}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Announcement Type</Label>
          <Select
            value={formData.announcement_type}
            onValueChange={(value: AnnouncementType) => setFormData({ ...formData, announcement_type: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANNOUNCEMENT_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Urgency Level</Label>
          <Select
            value={formData.urgency_level}
            onValueChange={(value: UrgencyLevel) => setFormData({ ...formData, urgency_level: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {URGENCY_LEVEL_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Call to Action (Optional)</Label>
        <div className="grid grid-cols-2 gap-4">
          <Input
            placeholder="Button label (e.g., Learn More)"
            value={formData.cta_label}
            onChange={(e) => setFormData({ ...formData, cta_label: e.target.value })}
          />
          <Input
            placeholder="URL (e.g., https://...)"
            value={formData.cta_url}
            onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          Target Audience
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {AUDIENCE_ROLE_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox
                id={`audience-${option.value}`}
                checked={formData.audience_roles.includes(option.value)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setFormData({ ...formData, audience_roles: [...formData.audience_roles, option.value] });
                  } else {
                    setFormData({ ...formData, audience_roles: formData.audience_roles.filter(r => r !== option.value) });
                  }
                }}
              />
              <Label htmlFor={`audience-${option.value}`} className="text-sm font-normal cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
      </div>

      <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg border">
        <Checkbox
          id="is-pinned"
          checked={formData.is_pinned}
          onCheckedChange={(checked) => setFormData({ ...formData, is_pinned: !!checked })}
        />
        <Label htmlFor="is-pinned" className="flex items-center gap-2 text-sm font-normal cursor-pointer">
          <Pin className="h-4 w-4" />
          Pin to top of announcements
        </Label>
      </div>
        {formData.audience_roles.length === 0 && (
          <p className="text-xs text-destructive">Please select at least one audience</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Send Mode</Label>
        <Select
          value={formData.status}
          onValueChange={(value: AnnouncementStatus) => setFormData({ ...formData, status: value })}
          disabled={currentStatus === "sent"}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value} disabled={currentStatus === "sent" && option.value !== "sent"}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {currentStatus === "sent" && (
          <p className="text-xs text-muted-foreground">This announcement has already been sent and cannot be reverted to draft.</p>
        )}
      </div>

      {formData.status === "scheduled" && (
        <div className="space-y-2">
          <Label htmlFor="scheduled_at">Schedule Date & Time</Label>
          <Input
            id="scheduled_at"
            type="datetime-local"
            value={formData.scheduled_at}
            onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
            min={new Date().toISOString().slice(0, 16)}
          />
        </div>
      )}

      {formData.status === "sent" && formData.audience_roles.length > 0 && (
        <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg">
          <Checkbox
            id="send-email"
            checked={formData.send_email}
            onCheckedChange={(checked) => setFormData({ ...formData, send_email: !!checked })}
          />
          <Label htmlFor="send-email" className="flex items-center gap-2 text-sm font-normal cursor-pointer">
            <Mail className="h-4 w-4" />
            Also send email notification to all {formData.audience_roles.map(r => AUDIENCE_ROLE_OPTIONS.find(o => o.value === r)?.label).join(", ")}
          </Label>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {formData.status === "sent" ? (
            <><Send className="h-4 w-4 mr-2" />Send Now</>
          ) : formData.status === "scheduled" ? (
            <><Clock className="h-4 w-4 mr-2" />Schedule</>
          ) : (
            <><FileText className="h-4 w-4 mr-2" />Save Draft</>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}
